import * as React from "react"
import {
  useChartBase,
  injectStyle,
  seriesColor,
  resolveColor,
  type NumberFormatter,
} from "./chartStyles"

/*
 * MARK V — DonutChart. Anel SVG quadrado via stroke-dasharray (sem path math).
 * Label/valor central como overlay HTML (tipo nítido). Legenda lista cada
 * fatia com valor e share. Porte fiel de
 * mark-v-design-system/components/charts/DonutChart.jsx.
 */

const CSS = `
.mv-donut{ display:flex; align-items:center; gap:24px; flex-wrap:wrap; }
.mv-donut__ring{ position:relative; flex-shrink:0; }
.mv-donut__svg{ transform:rotate(-90deg); display:block; }
.mv-donut__seg{ transition:opacity var(--markv-duration) var(--markv-ease); }
.mv-donut__center{ position:absolute; inset:0; display:flex; flex-direction:column;
  align-items:center; justify-content:center; text-align:center; gap:1px; }
.mv-donut__cv{ font-family:var(--markv-font-display); font-weight:var(--markv-weight-bold);
  font-size:var(--markv-text-xl); color:var(--markv-text-700); font-variant-numeric:tabular-nums; line-height:1; }
.mv-donut__cl{ font-size:var(--markv-text-2xs); color:var(--markv-text-400); text-transform:uppercase;
  letter-spacing:.06em; }
.mv-donut__legend{ display:flex; flex-direction:column; gap:9px; min-width:160px; flex:1; }
.mv-donut__row{ display:flex; align-items:center; gap:9px; font-size:var(--markv-text-sm); }
.mv-donut__dot{ width:10px; height:10px; border-radius:3px; flex-shrink:0; }
.mv-donut__k{ color:var(--markv-text-600); }
.mv-donut__v{ margin-left:auto; font-family:var(--markv-font-display); font-weight:var(--markv-weight-bold);
  font-variant-numeric:tabular-nums; color:var(--markv-text-700); }
.mv-donut__pct{ color:var(--markv-text-400); font-size:var(--markv-text-xs);
  font-variant-numeric:tabular-nums; min-width:38px; text-align:right; }
`

export type DonutDatum = {
  label: string
  value: number
  color?: string
}

export type DonutChartProps = {
  data: DonutDatum[]
  size?: number
  thickness?: number
  centerValue?: React.ReactNode
  centerLabel?: React.ReactNode
  format?: NumberFormatter
  showLegend?: boolean
  className?: string
}

export function DonutChart({
  data,
  size = 168,
  thickness = 22,
  centerValue,
  centerLabel,
  format = (v) => v,
  showLegend = true,
  className = "",
}: DonutChartProps) {
  useChartBase()
  React.useEffect(() => injectStyle("mv-donut-styles", CSS), [])

  const total = data.reduce((s, d) => s + d.value, 0) || 1
  const r = (size - thickness) / 2
  const C = 2 * Math.PI * r

  // frac de cada fatia + offset = -(soma das frações anteriores) * C.
  // Prefix-sum imutável (sem reassignment durante o render — react-hooks/immutability).
  const fracs = data.map((d) => d.value / total)
  const segments = fracs.map((frac, i) => ({
    frac,
    offset: -fracs.slice(0, i).reduce((s, f) => s + f, 0) * C,
  }))

  return (
    <div className={`mv-chart mv-donut ${className}`.trim()}>
      <div className="mv-donut__ring" style={{ width: size, height: size }}>
        <svg
          className="mv-donut__svg"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="var(--markv-chart-track)"
            strokeWidth={thickness}
          />
          {data.map((d, i) => {
            const { frac, offset } = segments[i]!
            const dash = `${(frac * C).toFixed(2)} ${(C - frac * C).toFixed(2)}`
            return (
              <circle
                key={i}
                className="mv-donut__seg"
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={resolveColor(d.color, seriesColor(i))}
                strokeWidth={thickness}
                strokeDasharray={dash}
                strokeDashoffset={offset}
                strokeLinecap="butt"
              >
                <title>{`${d.label}: ${format(d.value)}`}</title>
              </circle>
            )
          })}
        </svg>
        {(centerValue != null || centerLabel != null) && (
          <div className="mv-donut__center">
            {centerValue != null && <span className="mv-donut__cv">{centerValue}</span>}
            {centerLabel != null && <span className="mv-donut__cl">{centerLabel}</span>}
          </div>
        )}
      </div>
      {showLegend && (
        <div className="mv-donut__legend">
          {data.map((d, i) => (
            <div className="mv-donut__row" key={i}>
              <span
                className="mv-donut__dot"
                style={{ background: resolveColor(d.color, seriesColor(i)) }}
              />
              <span className="mv-donut__k">{d.label}</span>
              <span className="mv-donut__v">{format(d.value)}</span>
              <span className="mv-donut__pct">{Math.round((d.value / total) * 100)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
